import { useEffect, useRef } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { cssVar, onThemeChange } from '../theme.ts';

const SIZE = 20;

function step(zoom: number) {
  const target = 80 / zoom; // ~80px between labels
  const pow = Math.pow(10, Math.floor(Math.log10(target)));
  for (const m of [1, 2, 5, 10]) if (pow * m >= target) return pow * m;
  return pow * 10;
}

/** Top/left rulers. Drag out of a ruler to create a guide; drag a guide back onto it to delete. */
export function Rulers() {
  const show = useApp('ui', () => app.ui.showRulers);
  useApp(['camera', 'selection', 'doc'], () => app.camera.zoom);
  const top = useRef<HTMLCanvasElement>(null);
  const left = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!show) return;
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = app.viewportSize;
      const sel = app.editor.selectedNodes();
      let sb: { x: number; y: number; width: number; height: number } | null = null;
      for (const n of sel) {
        const b = app.doc.worldBounds(n.id);
        sb = sb ? { x: Math.min(sb.x, b.x), y: Math.min(sb.y, b.y), width: Math.max(sb.x + sb.width, b.x + b.width) - Math.min(sb.x, b.x), height: Math.max(sb.y + sb.height, b.y + b.height) - Math.min(sb.y, b.y) } : b;
      }
      const bg = cssVar('--bg') || '#fff',
        tick = cssVar('--ruler-tick') || '#c4c4c4',
        label = cssVar('--ruler-text') || '#8c8c8c',
        edge = cssVar('--border') || '#e6e6e6';
      for (const [c, horizontal] of [
        [top.current, true],
        [left.current, false],
      ] as const) {
        if (!c) continue;
        const len = horizontal ? width : height;
        c.width = (horizontal ? len : SIZE) * dpr;
        c.height = (horizontal ? SIZE : len) * dpr;
        c.style.width = `${horizontal ? len : SIZE}px`;
        c.style.height = `${horizontal ? SIZE : len}px`;
        const ctx = c.getContext('2d')!;
        // draw in "ruler space" (u along the ruler, v across); the vertical ruler transposes axes
        const base = () => (horizontal ? ctx.setTransform(dpr, 0, 0, dpr, 0, 0) : ctx.setTransform(0, dpr, dpr, 0, 0, 0));
        base();
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, len, SIZE);
        const z = app.camera.zoom;
        const off = horizontal ? app.camera.x : app.camera.y;
        // selection extent
        if (sb) {
          const a = (horizontal ? sb.x : sb.y) * z + off;
          const b = (horizontal ? sb.x + sb.width : sb.y + sb.height) * z + off;
          ctx.fillStyle = 'rgba(13,153,255,0.15)';
          ctx.fillRect(a, 0, b - a, SIZE);
        }
        const s = step(z);
        const start = Math.floor(-off / z / s) * s;
        ctx.strokeStyle = tick;
        ctx.fillStyle = label;
        ctx.font = '9px Inter, sans-serif';
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let v = start; v * z + off < len; v += s / 5) {
          const px = Math.round(v * z + off) + 0.5;
          const major = Math.abs(v / s - Math.round(v / s)) < 1e-6;
          ctx.moveTo(px, SIZE);
          ctx.lineTo(px, major ? SIZE - 8 : SIZE - 4);
          if (major) {
            if (horizontal) ctx.fillText(String(Math.round(v)), px + 2, 9);
            else {
              ctx.save();
              ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
              ctx.translate(9, px - 2);
              ctx.rotate(-Math.PI / 2);
              ctx.fillText(String(Math.round(v)), 0, 0);
              ctx.restore();
            }
          }
        }
        ctx.stroke();
        ctx.strokeStyle = edge;
        ctx.beginPath();
        ctx.moveTo(0, SIZE - 0.5);
        ctx.lineTo(len, SIZE - 0.5);
        ctx.stroke();
      }
    };
    draw();
    const off = app.onRender(draw);
    const offTheme = onThemeChange(draw);
    return () => {
      off();
      offTheme();
    };
  }, [show]);

  if (!show) return null;
  const startDrag = (axis: 'x' | 'y') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    app.setUI({ draggingGuide: { axis, index: -1, pos: 0 } });
  };
  return (
    <>
      <canvas ref={top} className="ruler top" onPointerDown={startDrag('x')} title="Drag to add a vertical guide" />
      <canvas ref={left} className="ruler left" onPointerDown={startDrag('y')} title="Drag to add a horizontal guide" />
      <div className="ruler-corner" />
    </>
  );
}
